import {makeApiRequest} from './api';
import {
  AggregationMethod,
  DashboardSection,
  ForecastTime,
  RateVolumeMode,
  TimeAggregation,
  UnitSystem,
} from './series';

export const H5_FORECAST_SCHEMA_VERSION = 1;
export const H5_OBSERVATION_SCHEMA_VERSION = 1;
export const H5_WEATHER_SCHEMA_VERSION = 1;
export const H5_WEATHER_LISTING_SCHEMA_VERSION = 2;
// Version 1 predates the resources read by valid time, so every one of its listings lists init
// times and parses as version 2 does.
const H5_WEATHER_LISTING_SCHEMA_VERSIONS = [1, H5_WEATHER_LISTING_SCHEMA_VERSION];

// The backend's MAX_FORECAST_RUNS (api/blueprints/hydroforecast/response_shaping.py).
export const H5_MAX_SELECTED_FORECASTS = 32;
// The backend's SUPPORTED_VARIABLE_TYPES (api/blueprints/hydroforecast/response_shaping.py): the
// observed quantities the forecast and observation routes are nested under, e.g. /flow/forecasts.
export type H5ObsType = 'flow';
// The backend's MAX_VALID_TIME_WINDOW (api/blueprints/hydroforecast/weather.py): the longest window
// weather observations and climatologies are read over.
export const H5_MAX_VALID_TIME_WINDOW_DAYS = 2 * 366;

export const H5_FORECAST_PRODUCT_BY_SECTION: Record<DashboardSection, string> = {
  [DashboardSection.ShortTerm]: 'flow-short-term',
  [DashboardSection.Seasonal]: 'flow-seasonal',
  [DashboardSection.Annual]: 'flow-annual',
};

// Passed through to fetch, so an unmounted section stops a read that is still in flight.
export type FetchOptions = {signal?: AbortSignal};

// The wire values of the backend's ForecastListingStatus and Visibility enums. h5.schema.test.ts
// checks them against the listing's JSON schema, which the backend snapshots into this directory.
export const H5_LISTING_STATUSES = ['available', 'unavailable'] as const;
export type H5ListingStatus = (typeof H5_LISTING_STATUSES)[number];
export const H5_MODEL_VISIBILITIES = ['primary', 'secondary', 'internal'] as const;
export type H5ModelVisibility = (typeof H5_MODEL_VISIBILITIES)[number];

export class UnsupportedH5SchemaError extends Error {}

export class H5RequestError extends Error {
  constructor(public readonly status: number) {
    super(`H5 request failed with status ${status}`);
  }
}

// A request the server rejected fails the same way again; only server errors, rate limits and
// failed connections can succeed on a retry.
export function isRetryableH5Error(error: unknown): boolean {
  if (error instanceof UnsupportedH5SchemaError) return false;
  if (error instanceof H5RequestError) return error.status >= 500 || error.status === 429;
  return true;
}

// Mirrors hf_models' ForecastVariable (hf/hf_models/hf_models/core/descriptor.py).
export type H5ForecastVariable = {
  name: string;
  unit: string;
  group: string;
  role: 'central-tendency' | 'bound';
  statistic: 'mean' | 'median' | 'quantile' | 'deterministic';
  quantile?: number;
};

// The wire form of each type is the same shape with its times still as ISO strings.
type H5ForecastListingFields<TForecast> = {
  modelType: string;
  modelId: string;
  product: string;
  visibility: H5ModelVisibility;
  schemaVersion: number;
  status: H5ListingStatus;
  // Newest first once parsed; the route sends them oldest first.
  availableForecasts: TForecast[];
};

// Mirrors hf_models' AssimilationConfig (hf/hf_models/hf_models/core/assimilation.py), keyed in
// camelCase; durations are ISO 8601.
export type H5AssimilationSettings = {
  learningRate: number;
  maxSteps: number;
  lookback: string;
  impactHorizon: string | null;
  minFlowThreshold: number;
};

// Mirrors hf_models' ModelConfigSignature (hf/hf_models/hf_models/core/config_facts.py): which
// pluggable slots the model's config fills, by slot path, and the class filling each.
export type H5ModelConfigSignature = {
  components: Record<string, string>;
  key: string;
};

export type H5ForecastListing<TForecast = ForecastTime> = H5ForecastListingFields<TForecast> &
  (
    | {
        status: 'available';
        displayAttributes: Record<string, string>;
        variables: H5ForecastVariable[];
        // Absent when the model does not assimilate, and from a backend too old to send it.
        assimilation?: H5AssimilationSettings;
        // Absent from a backend too old to send it.
        modelConfigSignature?: H5ModelConfigSignature;
        // The weather the model reads, in the order it reads it; empty for a model that reads none.
        // Absent from a backend too old to send it.
        weatherSources?: H5WeatherSource[];
      }
    | {status: 'unavailable'; reason: string}
  );

type H5ForecastListingResponse = H5ForecastListing<{initTime: string; issueTime: string}>;

export type H5ForecastDataRequest = {
  modelType: string;
  modelId: string;
  projectId: string;
  initTimes: string[];
  validTimeStartDate: string;
  validTimeEndDate: string;
  columns: string[];
  unitSystem: UnitSystem;
  timeAggregation: TimeAggregation;
  aggregationMethod: AggregationMethod;
  rateVolumeMode: Exclude<RateVolumeMode, 'level'>;
  // The route resolves a zone when this is omitted.
  timeZone?: string;
};

export type H5ForecastFrame<TTime = Date> = {
  initTime: TTime;
  issueTime: TTime;
  validTimes: TTime[];
  values: Record<string, (number | null)[]>;
  variables: H5ForecastVariable[];
};

export type H5ForecastData<TTime = Date> = {
  schemaVersion: number;
  modelType: string;
  modelId: string;
  timeZone: string;
  forecasts: H5ForecastFrame<TTime>[];
};

type H5ForecastDataResponse = H5ForecastData<string>;

function requireSupportedSchema(schemaVersion: number, ...supportedVersions: number[]): void {
  if (!supportedVersions.includes(schemaVersion)) {
    throw new UnsupportedH5SchemaError(`Unsupported H5 schema version ${schemaVersion}`);
  }
}

function requireSuccessfulResponse(status?: number): void {
  if (status !== undefined && status >= 400) {
    throw new H5RequestError(status);
  }
}

// Every run of a response is drawn as one time series, so a variable has to keep one unit across
// them.
function requireConsistentUnits(frames: {variables: {name: string; unit: string}[]}[]): void {
  const unitsByVariable = new Map<string, string>();
  for (const frame of frames) {
    for (const variable of frame.variables) {
      const unit = unitsByVariable.get(variable.name);
      if (unit !== undefined && unit !== variable.unit) {
        throw new UnsupportedH5SchemaError(`Inconsistent forecast units for ${variable.name}`);
      }
      unitsByVariable.set(variable.name, variable.unit);
    }
  }
}

export async function fetchH5ForecastListings(
  locationId: string,
  obsType: H5ObsType,
  initTimeRange: [Date, Date],
  {signal}: FetchOptions = {}
): Promise<H5ForecastListing[]> {
  const response = await makeApiRequest<H5ForecastListingResponse[]>(
    `hydroforecast/locations/${locationId}/${obsType}/forecasts`,
    {
      initTimeStartDate: initTimeRange[0].toISOString(),
      initTimeEndDate: initTimeRange[1].toISOString(),
    },
    {signal}
  );
  requireSuccessfulResponse(response.status);
  return response.data.map((listing) => {
    requireSupportedSchema(listing.schemaVersion, H5_FORECAST_SCHEMA_VERSION);
    return {
      ...listing,
      availableForecasts: listing.availableForecasts
        .map(({initTime, issueTime}) => ({
          initializationTime: new Date(initTime),
          issueTime: new Date(issueTime),
        }))
        .reverse(),
    };
  });
}

export async function fetchH5ForecastData(
  locationId: string,
  obsType: H5ObsType,
  request: H5ForecastDataRequest,
  {signal}: FetchOptions = {}
): Promise<H5ForecastData> {
  const response = await makeApiRequest<H5ForecastDataResponse>(
    `hydroforecast/locations/${locationId}/${obsType}/forecasts/data`,
    null,
    {method: 'POST', body: JSON.stringify(request), signal}
  );
  requireSuccessfulResponse(response.status);
  requireSupportedSchema(response.data.schemaVersion, H5_FORECAST_SCHEMA_VERSION);
  requireConsistentUnits(response.data.forecasts);
  return {
    ...response.data,
    forecasts: response.data.forecasts.map((forecast) => ({
      ...forecast,
      initTime: new Date(forecast.initTime),
      issueTime: new Date(forecast.issueTime),
      validTimes: forecast.validTimes.map((validTime) => new Date(validTime)),
    })),
  };
}

// --- Observations ----------------------------------------------------------
export const H5_OBSERVATION_VARIANTS = ['raw', 'cleaned'] as const;
export type H5ObservationVariant = (typeof H5_OBSERVATION_VARIANTS)[number];

export type H5ObservationVariable = {
  name: string;
  unit: string;
  group: string;
  role: 'observation';
  observationVariant: H5ObservationVariant;
};

export type H5ObservationDataRequest = {
  projectId: string;
  validTimeStartDate: string;
  validTimeEndDate: string;
  unitSystem: UnitSystem;
  timeAggregation: TimeAggregation;
  aggregationMethod: AggregationMethod;
  rateVolumeMode: 'rate' | 'volume';
  timeZone: string;
};

export type H5ObservationData<TTime = Date> = {
  schemaVersion: number;
  timeZone: string;
  validTimes: TTime[];
  values: Record<string, (number | null)[]>;
  variables: H5ObservationVariable[];
};

export async function fetchH5ObservationData(
  locationId: string,
  obsType: H5ObsType,
  request: H5ObservationDataRequest,
  {signal}: FetchOptions = {}
): Promise<H5ObservationData | null> {
  const response = await makeApiRequest<H5ObservationData<string> | null>(
    `hydroforecast/locations/${locationId}/${obsType}/observations/data`,
    null,
    {method: 'POST', body: JSON.stringify(request), signal}
  );
  requireSuccessfulResponse(response.status);
  // A configured series with no samples has empty arrays; null means no reference is configured.
  if (response.data === null) return null;
  requireSupportedSchema(response.data.schemaVersion, H5_OBSERVATION_SCHEMA_VERSION);
  return {
    ...response.data,
    validTimes: response.data.validTimes.map((validTime) => new Date(validTime)),
  };
}

export const listingKey = (listing: Pick<H5ForecastListing, 'modelType' | 'modelId'>) =>
  `${listing.modelType}:${listing.modelId}`;

// Every listing the route returned for the section's product. The route lists internal models
// only to internal users, so every listing that comes back can be offered.
export function forecastListingsForSection(
  listings: H5ForecastListing[] | undefined,
  section: DashboardSection
): H5ForecastListing[] {
  const product = H5_FORECAST_PRODUCT_BY_SECTION[section];
  return (listings ?? []).filter((listing) => listing.product === product);
}

// The model a section starts on. Never an internal one: those are picked on purpose.
export function defaultListing(
  sectionListings: H5ForecastListing[]
): H5ForecastListing | undefined {
  const candidates = sectionListings.filter((listing) => listing.visibility !== 'internal');
  return (
    candidates.find((listing) => listing.visibility === 'primary') ??
    candidates.find((listing) => listing.status === 'available') ??
    candidates[0]
  );
}

// --- Weather ---------------------------------------------------------------
export const H5_WEATHER_PRODUCT_BY_SECTION: Record<DashboardSection, string> = {
  [DashboardSection.ShortTerm]: 'weather-short-term',
  [DashboardSection.Seasonal]: 'weather-seasonal',
  [DashboardSection.Annual]: 'weather-annual',
};

// The wire values of the backend's WeatherSource and RequestKind enums, and WeatherVariable's and
// ClimatologyVariable's group. h5.schema.test.ts checks them against the weather JSON schemas. A
// source id is also the legacy Source value the chart styles it by.
export const H5_WEATHER_SOURCES = [
  'gefs',
  'era5-land',
  'era5',
  'gfs',
  'hrrr',
  'hrdps',
  'ifs-ens',
  'imerg',
  'mrms',
] as const;
export type H5WeatherSource = (typeof H5_WEATHER_SOURCES)[number];
export const H5_WEATHER_KINDS = ['forecast', 'observation', 'climatology'] as const;
export type H5WeatherKind = (typeof H5_WEATHER_KINDS)[number];
export const H5_WEATHER_VARIABLE_GROUPS = ['temperature', 'precipitation'] as const;
export type H5WeatherVariableGroup = (typeof H5_WEATHER_VARIABLE_GROUPS)[number];

export type H5WeatherVariable = {
  name: string;
  unit: string;
  group: H5WeatherVariableGroup;
};

// A climatology's mean and median are central tendencies; its other quantiles bound bands around
// them.
export type H5ClimatologyVariable = {
  name: string;
  unit: string;
  group: H5WeatherVariableGroup;
  role: 'central-tendency' | 'bound';
  statistic: 'mean' | 'median' | 'quantile';
  quantile?: number | null;
};

type H5WeatherListingFields = {
  weatherId: string;
  source: H5WeatherSource;
  dataset: string;
  product: string;
  kind: H5WeatherKind;
  status: H5ListingStatus;
};

export type H5WeatherListing = H5WeatherListingFields & {
  // Newest first once parsed; the route sends them oldest first. A weather run has no issue
  // time of its own, so each is dated by its init. Observations and climatologies, read by valid
  // time, have none.
  availableForecasts: ForecastTime[];
} & (
    | {status: 'available'; variables: (H5WeatherVariable | H5ClimatologyVariable)[]}
    | {status: 'unavailable'; reason: string}
  );

type H5WeatherListingResponse = H5WeatherListingFields & {
  reason?: string;
  variables?: (H5WeatherVariable | H5ClimatologyVariable)[];
  availability:
    | {kind: 'init-times'; availableForecasts: {initTime: string}[]}
    | {kind: 'valid-times'; start?: string; end?: string}
    | {kind: 'climatology'; periodStart?: string; periodEnd?: string};
};

export type H5WeatherDataRequest = {
  projectId: string;
  weatherId: string;
  // A forecast's runs. Observations and climatologies are read by valid time, so take none.
  initTimes?: string[];
  validTimeStartDate: string;
  validTimeEndDate: string;
  unitSystem: UnitSystem;
  timeAggregation: TimeAggregation;
  aggregationMethod: AggregationMethod;
  rateVolumeMode: Exclude<RateVolumeMode, 'level'>;
  timeZone: string;
};

export type H5WeatherForecastFrame<TTime = Date> = {
  initTime: TTime;
  validTimes: TTime[];
  values: Record<string, (number | null)[]>;
  variables: H5WeatherVariable[];
};

type H5WeatherDataFields = {
  schemaVersion: number;
  weatherId: string;
  source: H5WeatherSource;
  dataset: string;
  timeZone: string;
};

export type H5WeatherForecastData<TTime = Date> = H5WeatherDataFields & {
  kind: 'forecast';
  forecasts: H5WeatherForecastFrame<TTime>[];
};

export type H5WeatherObservationData<TTime = Date> = H5WeatherDataFields & {
  kind: 'observation';
  validTimes: TTime[];
  values: Record<string, (number | null)[]>;
  variables: H5WeatherVariable[];
};

export type H5WeatherClimatologyData<TTime = Date> = H5WeatherDataFields & {
  kind: 'climatology';
  validTimes: TTime[];
  values: Record<string, (number | null)[]>;
  variables: H5ClimatologyVariable[];
};

export type H5WeatherData<TTime = Date> =
  | H5WeatherForecastData<TTime>
  | H5WeatherObservationData<TTime>
  | H5WeatherClimatologyData<TTime>;

export async function fetchH5WeatherListings(
  locationId: string,
  projectId: string,
  initTimeRange: [Date, Date],
  {signal}: FetchOptions = {}
): Promise<H5WeatherListing[]> {
  const response = await makeApiRequest<{
    schemaVersion: number;
    weather: H5WeatherListingResponse[];
  }>(
    `hydroforecast/locations/${locationId}/weather`,
    {
      projectId,
      initTimeStartDate: initTimeRange[0].toISOString(),
      initTimeEndDate: initTimeRange[1].toISOString(),
    },
    {signal}
  );
  requireSuccessfulResponse(response.status);
  requireSupportedSchema(response.data.schemaVersion, ...H5_WEATHER_LISTING_SCHEMA_VERSIONS);
  return response.data.weather.map(({availability, reason, variables, ...listing}) => {
    const availableForecasts =
      availability.kind === 'init-times'
        ? availability.availableForecasts
            .map(({initTime}) => ({
              initializationTime: new Date(initTime),
              issueTime: new Date(initTime),
            }))
            .reverse()
        : [];
    return listing.status === 'available'
      ? {...listing, status: 'available', variables: variables ?? [], availableForecasts}
      : {...listing, status: 'unavailable', reason: reason ?? '', availableForecasts};
  });
}

export async function fetchH5WeatherData(
  locationId: string,
  request: H5WeatherDataRequest,
  {signal}: FetchOptions = {}
): Promise<H5WeatherData> {
  const response = await makeApiRequest<H5WeatherData<string>>(
    `hydroforecast/locations/${locationId}/weather/data`,
    null,
    {method: 'POST', body: JSON.stringify(request), signal}
  );
  requireSuccessfulResponse(response.status);
  const weatherData = response.data;
  requireSupportedSchema(weatherData.schemaVersion, H5_WEATHER_SCHEMA_VERSION);
  if (weatherData.kind !== 'forecast') {
    return {
      ...weatherData,
      validTimes: weatherData.validTimes.map((validTime) => new Date(validTime)),
    };
  }
  requireConsistentUnits(weatherData.forecasts);
  return {
    ...weatherData,
    forecasts: weatherData.forecasts.map((forecast) => ({
      ...forecast,
      initTime: new Date(forecast.initTime),
      validTimes: forecast.validTimes.map((validTime) => new Date(validTime)),
    })),
  };
}

// The resources of one kind the route returned for the section's product. The route leaves out
// sources it can't read, so every listing that comes back can be drawn once available.
export function weatherListingsForSection(
  listings: H5WeatherListing[] | undefined,
  section: DashboardSection,
  kind: H5WeatherKind = 'forecast'
): H5WeatherListing[] {
  const product = H5_WEATHER_PRODUCT_BY_SECTION[section];
  return (listings ?? []).filter((listing) => listing.product === product && listing.kind === kind);
}
